import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { Channel, SelectChannelResponse, VerifyCodeRequest, SuccessLoginResponse } from '../../../shared/interfaces';

@Injectable({
  providedIn: 'root',
})
export class MfaService {
  // --------------- Propiedades --------------- //
  private readonly apiUrl = environment.apiUrl;
  private _activeChallenge = signal<SelectChannelResponse | null>(null);
  private _availableChannels = signal<Channel[]>([]);

  activeChallenge = this._activeChallenge.asReadonly();

  /** Canales disponibles para verificación MFA */
  availableChannels = this._availableChannels.asReadonly();

  setActiveChallenge(value: SelectChannelResponse | null) {
    this._activeChallenge.set(value);
  }

  setAvailableChannels(value: Channel[]) {
    this._availableChannels.set(value);
  }

  // --------------- Inyecciones --------------- //
  private readonly http = inject(HttpClient);

  // --------------- Metodos --------------- //

  /**
   * Selecciona un canal MFA y solicita el envio de codigo
   * POST /api/v1/mfa/send
   * @param data - (type: { channelId: string | number }) con el ID del canal seleccionado
   * @returns Observable - (type: SelectChannelResponse) con type, value y cooldown
   */
  sendCode(data: { channelId: string | number }): Observable<SelectChannelResponse> {
    return this.http.post<SelectChannelResponse>(`${this.apiUrl}/mfa/send`, data).pipe(
      tap((response: SelectChannelResponse) => {
        this.setActiveChallenge(response);
      }),
      catchError((err) => {
        return throwError(() => err.error);
      }),
    );
  }

  /**
   * Verifica el codigo OTP/TOTP ingresado por el usuario
   * POST /api/v1/mfa/verify
   * @param data - (type: VerifyCodeRequest) con code y rememberDevice opcional
   * @returns Observable - (type: SuccessLoginResponse) con verified: true si el codigo es correcto
   */
  verifyCode(data: VerifyCodeRequest): Observable<SuccessLoginResponse> {
    return this.http.post<SuccessLoginResponse>(`${this.apiUrl}/mfa/verify`, data).pipe(
      tap(() => {
        // Limpiar el challenge activo despues de verificar exitosamente
        this.setActiveChallenge(null);
      }),
      catchError((err) => {
        return throwError(() => err.error);
      }),
    );
  }

  /**
   * Reenvia el codigo en un challenge activo
   * POST /api/v1/mfa/resend
   * No requiere body - el challenge se identifica por el session token (header Authorization)
   * @returns Observable - (type: SelectChannelResponse) con cooldown actualizado
   */
  resendCode(): Observable<SelectChannelResponse> {
    return this.http.post<SelectChannelResponse>(`${this.apiUrl}/mfa/resend`, {}).pipe(
      tap((response: SelectChannelResponse) => {
        this.setActiveChallenge(response);
      }),
      catchError((err) => {
        return throwError(() => err.error);
      }),
    );
  }

  /**
   * Limpia el estado del servicio
   */
  clearMfaState(): void {
    this.setActiveChallenge(null);
    this.setAvailableChannels([]);
  }
}
